import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableFooter,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import {
  TaskSchedulesClient,
  TaskScheduleDto,
  TaskTemplatesClient,
  TaskTemplateDto,
  TaskScheduleAssignmentDto,
} from '../../app/services/api/tasksClient';
import CronExpressionPicker from '../../components/forms/CronExpressionPicker';
import { TableHeader } from '../../components/forms/TableHeader';
import { DeleteOutline } from '@mui/icons-material';

const schedulesClient = TaskSchedulesClient;
const templatesClient = TaskTemplatesClient;

const TaskScheduleDetails: React.FC = () => {
  const { t } = useTranslation('tasks');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo, franchises, stores } = useContext(UserContext);

  const isCreate = !id || id === 'new';
  const canCreate = hasPermissionTo ? hasPermissionTo(['TaskSchedulesCreate']) : false;
  const canEdit = hasPermissionTo ? hasPermissionTo(['TaskSchedulesEdit']) : false;

  const [templates, setTemplates] = useState<TaskTemplateDto[]>([]);
  const [schedule, setSchedule] = useState<TaskScheduleDto | null>(null);
  const [assignments, setAssignments] = useState<TaskScheduleAssignmentDto[]>([]);
  const [editingAssignmentIndex, setEditingAssignmentIndex] = useState<number | null>(null);
  const [editingAssignment, setEditingAssignment] = useState<TaskScheduleAssignmentDto | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [cronSummary, setCronSummary] = useState<string>('');
  const [formValues, setFormValues] = useState<{
    taskScheduleName: string;
    taskTemplateId: number | '';
    frequencyType: 'Daily' | 'Weekly' | 'Interval' | 'Monthly' | 'Custom';
    cronExpression: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  }>({
    taskScheduleName: '',
    taskTemplateId: '',
    frequencyType: 'Daily',
    cronExpression: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    templatesClient.getTemplates().then(setTemplates).catch(() => setTemplates([]));

    if (!isCreate && id) {
      const scheduleId = Number(id);
      schedulesClient.getSchedule(scheduleId).then(s => {
        setSchedule(s);
        setAssignments(s.assignments ?? []);

        setFormValues({
          taskScheduleName: s.taskScheduleName,
          taskTemplateId: s.taskTemplateId,
          frequencyType: 'Custom',
          cronExpression: s.cronExpression ?? '',
          startDate: s.startDate ?? '',
          endDate: s.endDate ?? '',
          isActive: s.isActive,
        });
        // human-readable text will be initialised by CronExpressionPicker via cronstrue
      }).catch(() => setSchedule(null));
    }
  }, [id, isCreate]);

  if (isCreate && !canCreate) {
    return (
      <PageArea>
        <PageTitle>{t('scheduleDetails.pageTitle', 'Task Schedule')}</PageTitle>
        <Pill text={t('scheduleDetails.noPermissionCreate', 'You do not have permission to create task schedules.')} />
      </PageArea>
    );
  }

  if (!isCreate && !canEdit) {
    return (
      <PageArea>
        <PageTitle>{t('scheduleDetails.pageTitle', 'Task Schedule')}</PageTitle>
        <Pill text={t('scheduleDetails.noPermissionEdit', 'You do not have permission to edit task schedules.')} />
      </PageArea>
    );
  }

  const fakePersonas: { id: number; name: string }[] = [
    { id: 1, name: 'Store Manager' },
    { id: 2, name: 'Assistant Manager' },
    { id: 3, name: 'Team Member' },
  ];

  const fakeUsers: { id: number; name: string }[] = [
    { id: 1001, name: 'Alex Smith' },
    { id: 1002, name: 'Jamie Lee' },
    { id: 1003, name: 'Taylor Brown' },
  ];

  const getPersonaName = (id: number | null | undefined) => {
    if (id == null) return null;
    return fakePersonas.find(p => p.id === id)?.name ?? null;
  };

  const getUserName = (id: number | null | undefined) => {
    if (id == null) return null;
    return fakeUsers.find(u => u.id === id)?.name ?? null;
  };

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formValues.taskScheduleName.trim()) e.taskScheduleName = t('scheduleDetails.errorRequired', 'Required');
    if (!formValues.taskTemplateId) e.taskTemplateId = t('scheduleDetails.errorRequired', 'Required');
    if (!formValues.startDate) e.startDate = t('scheduleDetails.errorRequired', 'Required');
    if (!formValues.cronExpression.trim()) e.cronExpression = t('scheduleDetails.errorCronRequired', 'CRON expression is required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const cronExpression = formValues.cronExpression;

    const dto: TaskScheduleDto = {
      id: schedule?.id ?? 0,
      taskScheduleName: formValues.taskScheduleName.trim(),
      taskTemplateId: Number(formValues.taskTemplateId),
      templateTask: schedule?.templateTask ?? templates.find(t => t.id === Number(formValues.taskTemplateId))!,
      startDate: formValues.startDate,
      endDate: formValues.endDate || null,
      cronExpression,
      isActive: formValues.isActive,
      assignments,
    };

    if (isCreate) {
      await schedulesClient.createSchedule(dto);
    } else if (schedule) {
      await schedulesClient.updateSchedule(schedule.id, dto);
    }

    navigate('/task-schedules');
  };

  const title = isCreate ? t('scheduleDetails.titleCreate', 'Create Task Schedule') : t('scheduleDetails.titleEdit', 'Edit Task Schedule');

  const handleAddAssignment = () => {
    const base: TaskScheduleAssignmentDto = {
      id: 0,
      taskScheduleId: schedule?.id ?? 0,
      franchiseId: 0,
      franchiseIds: [],
      scopeType: 'LOCATION',
      storeNumber: null,
      storeNumbers: [],
      personaId: null,
      personaIds: [],
      userId: null,
      userIds: [],
      assignedAtUtc: new Date().toISOString(),
      assignedByUserId: null,
      unassignedAtUtc: null,
      escalationRules: [],
      approverPersonaId: null,
      approverUserId: null,
    };
    setEditingAssignmentIndex(null);
    setEditingAssignment(base);
  };

  const handleEditAssignment = (index: number) => {
    const existing = assignments[index];
    if (!existing) return;
    setEditingAssignmentIndex(index);
    setEditingAssignment({ ...existing });
  };

  const handleAssignmentFieldChange = <K extends keyof TaskScheduleAssignmentDto>(field: K, value: TaskScheduleAssignmentDto[K]) => {
    setEditingAssignment(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveAssignment = () => {
    if (!editingAssignment) return;
    setAssignments(prev => {
      if (editingAssignmentIndex === null) {
        return [...prev, editingAssignment];
      }
      return prev.map((a, i) => (i === editingAssignmentIndex ? editingAssignment : a));
    });
    setEditingAssignment(null);
    setEditingAssignmentIndex(null);
  };

  const handleCancelAssignmentEdit = () => {
    setEditingAssignment(null);
    setEditingAssignmentIndex(null);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex === null) return;
    setAssignments(prev => prev.filter((_, i) => i !== deleteIndex));
    setDeleteIndex(null);
  };

  return (
    <PageArea>
      <Grid
        container
        spacing={5}
      >
        <Grid
          item
          xs={12}
        >
          <Grid
            container
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid item>
              <PageTitle>{title}</PageTitle>
            </Grid>
            <Grid item>
              <Grid container
                spacing={2}>
                <Grid item>
                  <Button variant='secondary'
                    onClick={() => navigate('/task-schedules')}>
                    {t('scheduleDetails.cancel', 'Cancel')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant='primary'
                    onClick={handleSave}>
                    {t('scheduleDetails.save', 'Save')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
        >
          <Section style={{ paddingLeft: 8, paddingRight: 8, paddingBottom: 8 }}>
            <Grid
              container
              spacing={3}
              alignItems='center'
            >
              <Grid
                item
                xs={12}
              >
                <h3>{t('scheduleDetails.sectionScheduleDetails', 'Schedule details')}</h3>
              </Grid>
            </Grid>

            <Grid container
              spacing={3}>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  fullWidth
                  size='small'
                  label={t('scheduleDetails.fieldScheduleName', 'Schedule name')}
                  value={formValues.taskScheduleName}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    taskScheduleName: e.target.value,
                  }))}
                  error={!!errors.taskScheduleName}
                  helperText={errors.taskScheduleName}
                />
              </Grid>

              <Grid item
                xs={12}
                md={4}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('scheduleDetails.fieldTaskTemplate', 'Task Template')}
                  value={formValues.taskTemplateId}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    taskTemplateId: e.target.value === '' ? '' : Number(e.target.value),
                  }))}
                  error={!!errors.taskTemplateId}
                  helperText={errors.taskTemplateId}
                >
                  <MenuItem value=''>{t('scheduleDetails.selectTemplate', 'Select template')}</MenuItem>
                  {templates.map(t => (
                    <MenuItem key={t.id}
                      value={t.id}>{t.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.isActive}
                      onChange={e => setFormValues(v => ({
                        ...v,
                        isActive: e.target.checked,
                      }))}
                    />
                  }
                  label={t('scheduleDetails.fieldScheduleActive', 'Schedule is active')}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  size='small'
                  label={t('scheduleDetails.fieldStartDate', 'Start date')}
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={formValues.startDate}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    startDate: e.target.value,
                  }))}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  size='small'
                  label={t('scheduleDetails.fieldEndDate', 'End date')}
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={formValues.endDate}
                  onChange={e => setFormValues(v => ({
                    ...v,
                    endDate: e.target.value,
                  }))}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>


        <Grid
          item
          xs={12}
        >
          <Section style={{ paddingLeft: 8, paddingRight: 8, paddingBottom: 8 }}>
            <Grid
              container
              spacing={3}
              alignItems='center'
            >
              <Grid
                item
                xs={12}
              >
                <h3 style={{ display: 'inline-block', marginRight: '8px' }}>{t('scheduleDetails.scheduleSummary', 'Schedule summary:')}</h3>
                <span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#666' }}>
                  {cronSummary || t('scheduleDetails.noScheduleDefined', 'No schedule defined yet.')}
                </span>
              </Grid>
            </Grid>

            <Grid container
              spacing={3}>

              <Grid
                item
                xs={12}
              >

                {/* Time-based fields are now handled by CronExpressionPicker */}
                <CronExpressionPicker
                  value={formValues.cronExpression}
                  onChange={(cron: string) => setFormValues(v => ({
                    ...v,
                    cronExpression: cron,
                  }))}
                  onHumanReadableChange={(text: string) => setCronSummary(text)}
                  disableDate={false}
                />
              </Grid>
            </Grid>


            <Grid
              item
              xs={12}
            >
              <Section style={{ boxShadow: 'none', marginTop: '16px' }}>
                <Grid
                  container
                  justifyContent='space-between'
                  alignItems='center'
                  style={{ marginBottom: 8, marginTop: 8 }}
                >
                  <Grid item>
                    <TableHeader>{t('scheduleDetails.assignments', 'Assignments')}</TableHeader>
                  </Grid>
                  <Grid item>
                    <Button
                      variant='secondary'
                      size='md'
                      style={{ marginRight: '12px' }}
                      onClick={handleAddAssignment}
                    >
                      {t('scheduleDetails.addAssignment', 'Add Assignment')}
                    </Button>
                  </Grid>
                </Grid>

                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('scheduleDetails.columnScope', 'Scope')}</TableCell>
                      <TableCell>{t('scheduleDetails.columnEscalations', 'Escalations')}</TableCell>
                      <TableCell>{t('scheduleDetails.columnSummary', 'Summary')}</TableCell>
                      <TableCell align='right'></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.map((a, index) => {
                      const franchiseName =
                        franchises.find(f => f.franchiseId === a.franchiseId)?.franchiseName ??
                        t('scheduleDetails.franchiseFallback', 'Franchise {{id}}', { id: a.franchiseId });

                      const storeCount = a.storeNumbers?.length ?? 0;
                      const hasAllStores = storeCount === 0;

                      const firstStoreNumber = a.storeNumbers?.[0];
                      const firstStore = stores.find(s => s.storeNumber === firstStoreNumber);
                      const storeLabel = firstStore
                        ? (firstStore).storeName ?? firstStore.storeNumber
                        : firstStoreNumber;

                      const userName = getUserName(a.userId ?? null);
                      const personaName = getPersonaName(a.personaId ?? null);

                      let summary: string;
                      if (a.scopeType === 'LOCATION') {
                        const locationPart = hasAllStores
                          ? t('scheduleDetails.summaryAllStores', 'all stores')
                          : storeCount === 1
                            ? t('scheduleDetails.summaryStoreSingle', 'store {{store}}', { store: storeLabel })
                            : t('scheduleDetails.summaryStoreCount', '{{count}} stores', { count: storeCount });

                        const whoPart = a.userId
                          ? (userName ? t('scheduleDetails.summaryUserByName', 'user {{name}}', { name: userName }) : t('scheduleDetails.summaryUserById', 'user {{id}}', { id: a.userId }))
                          : a.personaId
                            ? (personaName ? t('scheduleDetails.summaryPersonaByName', 'persona {{name}}', { name: personaName }) : t('scheduleDetails.summaryPersonaById', 'persona {{id}}', { id: a.personaId }))
                            : t('scheduleDetails.summaryAnyColleague', 'any colleague');

                        summary = t('scheduleDetails.summaryLocation', '{{franchise}} - {{locationPart}}, completed by {{whoPart}}', { franchise: franchiseName, locationPart, whoPart });
                      } else {
                        // COLLEAGUE
                        const filteredStorePart = hasAllStores
                          ? t('scheduleDetails.summaryAllStores', 'all stores')
                          : storeCount === 1
                            ? t('scheduleDetails.summaryStoreSingle', 'store {{store}}', { store: storeLabel })
                            : t('scheduleDetails.summaryStoreCount', '{{count}} stores', { count: storeCount });

                        const userPart = a.userId
                          ? (userName ? t('scheduleDetails.summaryUserByName', 'user {{name}}', { name: userName }) : t('scheduleDetails.summaryUserById', 'user {{id}}', { id: a.userId }))
                          : a.personaId
                            ? (personaName ? t('scheduleDetails.summaryPersonaByName', 'persona {{name}}', { name: personaName }) : t('scheduleDetails.summaryPersonaById', 'persona {{id}}', { id: a.personaId }))
                            : t('scheduleDetails.summaryAllUsers', 'all users');

                        summary = t('scheduleDetails.summaryColleague', '{{userPart}}, filtered by {{franchise}} - {{filteredStorePart}}', { userPart, franchise: franchiseName, filteredStorePart });
                      }


                      const hasEscalations = (a.escalationRules?.length ?? 0) > 0;

                      return (
                        <TableRow
                          key={a.id ?? index}
                          hover
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleEditAssignment(index)}
                        >
                          <TableCell>
                            {a.scopeType === 'LOCATION' && t('scheduleDetails.scopeByLocation', 'By location')}
                            {a.scopeType === 'COLLEAGUE' && t('scheduleDetails.scopeByColleague', 'By colleague')}
                          </TableCell>
                          <TableCell>{hasEscalations ? t('scheduleDetails.hasEscalations', 'Has escalations') : '-'}</TableCell>
                          <TableCell>{summary}</TableCell>
                          <TableCell
                            align='right'
                            onClick={e => {
                              e.stopPropagation();
                              setDeleteIndex(index);
                            }}
                            style={{ color: 'red', cursor: 'pointer' }}
                          >
                            <DeleteOutline />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3}>
                        {/* Reserved for future summary / pagination */}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </Section>
            </Grid>
          </Section>
        </Grid>
      </Grid >

      {/* Add / Edit assignment rule dialog */}
      < Dialog
        open={!!editingAssignment}
        onClose={handleCancelAssignmentEdit}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>{editingAssignmentIndex === null ? t('scheduleDetails.dialogAddAssignment', 'Add assignment rule') : t('scheduleDetails.dialogEditAssignment', 'Edit assignment rule')}</DialogTitle>
        <DialogContent>
          {editingAssignment && (
            <Grid
              container
              spacing={3}
              marginTop={1}
            >
              {/* Franchise selection - appears first */}
              <Grid
                item
                xs={12}
              >
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('scheduleDetails.fieldFranchise', 'Franchise')}
                  helperText={t('scheduleDetails.helperSelectFranchise', 'Select the franchise for this assignment.')}
                  value={editingAssignment.franchiseId ?? 0}
                  onChange={e => {
                    const value = Number(e.target.value);
                    handleAssignmentFieldChange('franchiseId', value);
                    handleAssignmentFieldChange('franchiseIds', [value]);
                    // Clear store selections when franchise changes
                    handleAssignmentFieldChange('storeNumbers', []);
                  }}
                >
                  {franchises.map(f => (
                    <MenuItem key={f.franchiseId}
                      value={f.franchiseId}>{f.franchiseName}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
              >
                <h3>{t('scheduleDetails.howTasksGenerated', 'How should tasks be generated?')}</h3>
                <RadioGroup
                  row
                  value={editingAssignment.scopeType}
                  onChange={e => handleAssignmentFieldChange('scopeType', e.target.value as TaskScheduleAssignmentDto['scopeType'])}
                >
                  <FormControlLabel
                    value='LOCATION'
                    control={<Radio size='small' />}
                    label={t('scheduleDetails.radioPerLocation', 'One task per location')}
                  />
                  <FormControlLabel
                    value='COLLEAGUE'
                    control={<Radio size='small' />}
                    label={t('scheduleDetails.radioPerColleague', 'One task per colleague')}
                  />
                </RadioGroup>
              </Grid>

              {/* BY LOCATION: Tasks generated per store, optionally restrict who can complete */}
              {editingAssignment.scopeType === 'LOCATION' && (
                <>
                  <Grid
                    item
                    xs={12}
                  >
                    <h3>{t('scheduleDetails.whichLocations', 'Which locations will have tasks generated?')}</h3>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label={t('scheduleDetails.fieldStores', 'Stores')}
                      helperText={t('scheduleDetails.helperSelectStoresLocation', 'Select specific stores, or leave empty to generate tasks for all stores in the franchise.')}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => {
                          const nums = selected as string[];
                          if (nums.length === 0) return t('scheduleDetails.allStoresRenderValue', 'All stores');
                          return nums.join(', ');
                        },
                      }}
                      value={editingAssignment.storeNumbers ?? []}
                      onChange={e => {
                        const value = (e.target.value as unknown) as string[];
                        handleAssignmentFieldChange('storeNumbers', value);
                      }}
                    >
                      {stores
                        .filter(s => !editingAssignment.franchiseId || s.franchiseId === editingAssignment.franchiseId)
                        .map(s => (
                          <MenuItem key={s.storeNumber}
                            value={s.storeNumber}>{s.storeName}</MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <h3>{t('scheduleDetails.whoCanComplete', 'Who can complete the task? (Optional)')}</h3>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label={t('scheduleDetails.fieldRole', 'Role')}
                      helperText={t('scheduleDetails.helperRestrictRole', 'Restrict completion to a specific role.')}
                      value={editingAssignment.personaId ?? ''}
                      onChange={e => handleAssignmentFieldChange('personaId', e.target.value ? Number(e.target.value) : null)}
                    >
                      <MenuItem value=''>{t('scheduleDetails.anyColleagueOption', 'Any colleague')}</MenuItem>
                      {fakePersonas.map(p => (
                        <MenuItem
                          key={p.id}
                          value={p.id}
                        >{p.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label={t('scheduleDetails.fieldSpecificUser', 'Specific user')}
                      helperText={t('scheduleDetails.helperRestrictUser', 'Restrict completion to a specific user.')}
                      value={editingAssignment.userId ?? ''}
                      onChange={e => handleAssignmentFieldChange('userId', e.target.value ? Number(e.target.value) : null)}
                    >
                      <MenuItem value=''>{t('scheduleDetails.anyColleagueOption', 'Any colleague')}</MenuItem>
                      {fakeUsers.map(u => (
                        <MenuItem
                          key={u.id}
                          value={u.id}
                        >{u.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}

              {/* BY COLLEAGUE: Tasks generated per user, optionally restrict by location */}
              {editingAssignment.scopeType === 'COLLEAGUE' && (
                <>
                  <Grid
                    item
                    xs={12}
                  >
                    <h3>{t('scheduleDetails.whichColleagues', 'Which colleagues will have tasks generated?')}</h3>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label={t('scheduleDetails.fieldRole', 'Role')}
                      helperText={t('scheduleDetails.helperRoleColleague', 'Generate tasks for colleagues with this role.')}
                      value={editingAssignment.personaId ?? ''}
                      onChange={e => handleAssignmentFieldChange('personaId', e.target.value ? Number(e.target.value) : null)}
                    >
                      <MenuItem value=''>{t('scheduleDetails.allRoles', 'All roles')}</MenuItem>
                      {fakePersonas.map(p => (
                        <MenuItem
                          key={p.id}
                          value={p.id}
                        >{p.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label={t('scheduleDetails.fieldSpecificUser', 'Specific user')}
                      helperText={t('scheduleDetails.helperUserColleague', 'Generate a task for this specific user only.')}
                      value={editingAssignment.userId ?? ''}
                      onChange={e => handleAssignmentFieldChange('userId', e.target.value ? Number(e.target.value) : null)}
                    >
                      <MenuItem value=''>{t('scheduleDetails.allUsersMatching', 'All users matching criteria')}</MenuItem>
                      {fakeUsers.map(u => (
                        <MenuItem
                          key={u.id}
                          value={u.id}
                        >{u.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <h3>{t('scheduleDetails.restrictLocations', 'Restrict to specific locations? (Optional)')}</h3>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label={t('scheduleDetails.fieldStores', 'Stores')}
                      helperText={t('scheduleDetails.helperRestrictStoresColleague', 'Only colleagues assigned to these stores will receive tasks. Leave empty to include all stores.')}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => {
                          const nums = selected as string[];
                          if (nums.length === 0) return t('scheduleDetails.allStoresRenderValue', 'All stores');
                          return nums.join(', ');
                        },
                      }}
                      value={editingAssignment.storeNumbers ?? []}
                      onChange={e => {
                        const value = (e.target.value as unknown) as string[];
                        handleAssignmentFieldChange('storeNumbers', value);
                      }}
                    >
                      {stores
                        .filter(s => !editingAssignment.franchiseId || s.franchiseId === editingAssignment.franchiseId)
                        .map(s => (
                          <MenuItem key={s.storeNumber}
                            value={s.storeNumber}>{s.storeName}</MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                </>
              )}

              {/* Approver Section */}
              <Grid
                item
                xs={12}
              >
                <h3>{t('scheduleDetails.approverOptional', 'Approver (Optional)')}</h3>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('scheduleDetails.fieldApproverRole', 'Approver Role')}
                  helperText={t('scheduleDetails.helperApproverRole', 'Specify a role that must approve completed tasks.')}
                  value={editingAssignment.approverPersonaId ?? ''}
                  onChange={e => {
                    handleAssignmentFieldChange('approverPersonaId', e.target.value ? Number(e.target.value) : null);
                    // Clear user if role selected
                    if (e.target.value) {
                      handleAssignmentFieldChange('approverUserId', null);
                    }
                  }}
                  disabled={!!editingAssignment.approverUserId}
                >
                  <MenuItem value=''>{t('scheduleDetails.noApproverRequired', 'No approver required')}</MenuItem>
                  {fakePersonas.map(p => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                    >{p.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('scheduleDetails.fieldSpecificApprover', 'Or Specific Approver')}
                  helperText={t('scheduleDetails.helperApproverUser', 'Specify a user that must approve completed tasks.')}
                  value={editingAssignment.approverUserId ?? ''}
                  onChange={e => {
                    handleAssignmentFieldChange('approverUserId', e.target.value ? Number(e.target.value) : null);
                    // Clear role if user selected
                    if (e.target.value) {
                      handleAssignmentFieldChange('approverPersonaId', null);
                    }
                  }}
                  disabled={!!editingAssignment.approverPersonaId}
                >
                  <MenuItem value=''>{t('scheduleDetails.noApproverRequired', 'No approver required')}</MenuItem>
                  {fakeUsers.map(u => (
                    <MenuItem
                      key={u.id}
                      value={u.id}
                    >{u.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant='secondary'
            onClick={handleCancelAssignmentEdit}
          >{t('scheduleDetails.cancel', 'Cancel')}</Button>
          <Button
            variant='primary'
            onClick={handleSaveAssignment}
          >{t('scheduleDetails.save', 'Save')}</Button>
        </DialogActions>
      </Dialog >

      {/* Delete confirmation dialog */}
      < Dialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
      >
        <DialogTitle>{t('scheduleDetails.deleteAssignmentTitle', 'Delete assignment')}</DialogTitle>
        <DialogContent>
          {t('scheduleDetails.deleteAssignmentConfirm', 'Are you sure you want to delete this assignment rule?')}
        </DialogContent>
        <DialogActions>
          <Button
            variant='secondary'
            onClick={() => setDeleteIndex(null)}
          >{t('scheduleDetails.cancel', 'Cancel')}</Button>
          <Button
            variant='primary'
            onClick={handleConfirmDelete}
          >{t('scheduleDetails.delete', 'Delete')}</Button>
        </DialogActions>
      </Dialog >
    </PageArea >
  );
};

export default TaskScheduleDetails;
